import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    AGURenderService,
    AGUSortOption,
    AGUToastService,
    AGUViewportService,
    AGUAppLocation,
    AGUAppModuleAccent,
    AGUTabStripTab,
    AGUSearchAttachment,
    AGUResultCount,
    FiltersFromEcho,
    FinderPageState,
    FinderTab,
    RevealDirection,
    InitialFinderState,
    IsJudgedKey,
    JudgedPills,
    PageResultCount,
    RemoveJudged,
    ResetFilters,
    StripQueryGroups,
    WithPage,
    WithQuery,
    type JudgedConstraint,
    FinderTabScrollMemory,
    OriginOf,
    AGUAdPlacement,
    AGUAdSessionService,
} from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { RaCompositionState } from '../../ra-composition.state';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import { GrantSort, ItemKind, ResearchGrantFacets, ResearchGrantFilterInput, ResearchGrantMatch, ResearchQueryInput } from '../../models/research.models';
import { BuildGrantFilterPills, CountActiveGrantFilterGroups, GRANT_QUERY_GROUP_FIELDS, RemoveGrantFilterGroup } from './grant-filters.component';
import { ActiveFilterPill } from '../../finder/active-filter-pills';
import { ActiveGroupsFrom, PhoneFinderChrome, ToSortOptions } from '../../finder/phone-finder-chrome';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { BrowseAllLabel, FinderAllEmptyCopy, FinderRecommendedEmptyCopy, LISTING_GONE_COPY, ListErrorCopy, STATE_NOUN, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import { DismissedSlots } from '../shared/dismissed-slots';
import { PinnedFirst } from '../shared/pinned-first';
import { NextResultsPanelId } from '../shared/panel-id';
import { RECOMMENDED_COUNT } from '../shared/recommended-count';
import { AssertRecommendationsRefreshed } from '../shared/recommendations-refreshed';

/** Views this module has. Narrows AGUAppLocation for every internal comparison. */
export type GrantFinderView = 'search' | 'detail';

const GRANT_KIND: ItemKind = 'grant';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const TRY_CHIPS: readonly string[] = [
    'Coastal resilience funding over $250K',
    'NOAA opportunities closing this fall',
    'Flood-risk grants in the Gulf Coast',
];

/** Tab order for the reveal direction: All then Recommended. */
const TAB_ORDER: ReadonlyArray<FinderTab> = ['all', 'recommended'];

const SORT_OPTIONS: ReadonlyArray<{ key: GrantSort; label: string }> = [
    { key: 'best', label: 'Best match' },
    { key: 'closingSoonest', label: 'Closing soonest' },
    { key: 'recentlyPosted', label: 'Recently posted' },
    { key: 'largestAward', label: 'Largest award' },
    { key: 'titleAZ', label: 'Title A-Z' },
];

/** "No constraint" shape for every ResearchGrantFilterInput field — arrays → [], booleans → false, everything else → undefined. Exported for the spec (D9/D10 echo/strip helpers). */
export const EMPTY_GRANT_FILTERS: ResearchGrantFilterInput = {
    awardMaxUSD: undefined,
    awardMinUSD: undefined,
    awardUnstatedOnly: false,
    closingAfter: undefined,
    closingBefore: undefined,
    eligibility: [],
    excludeGrantor: [],
    funderType: [],
    fromQuery: [],
    grantor: [],
    includeClosed: false,
    includeRolling: false,
    includeUnstatedAward: false,
    postedAfter: undefined,
    region: [],
    rollingOnly: false,
    recommendedOnly: false,
};

/** Strips every default/empty field so the server never sees e.g. `funderType: []` — grantFilterMapping.ts's `list()` helper throws on an empty array rather than treating it as "no constraint". Exported for the spec; the page's private `ActiveFiltersForServer` delegates here. */
export function ActiveGrantFiltersForServer(f: ResearchGrantFilterInput): ResearchGrantFilterInput {
    const out: ResearchGrantFilterInput = {};
    if (f.funderType && f.funderType.length > 0) out.funderType = f.funderType;
    if (f.grantor && f.grantor.length > 0) out.grantor = f.grantor;
    if (f.excludeGrantor && f.excludeGrantor.length > 0) out.excludeGrantor = f.excludeGrantor;
    if (f.eligibility && f.eligibility.length > 0) out.eligibility = f.eligibility;
    if (f.region && f.region.length > 0) out.region = f.region;
    if (f.postedAfter) out.postedAfter = f.postedAfter;
    if (f.closingBefore) out.closingBefore = f.closingBefore;
    if (f.closingAfter) out.closingAfter = f.closingAfter;
    if (f.includeRolling) out.includeRolling = true;
    if (f.rollingOnly) out.rollingOnly = true;
    if (f.awardMinUSD !== undefined && f.awardMinUSD !== null) out.awardMinUSD = f.awardMinUSD;
    if (f.awardMaxUSD !== undefined && f.awardMaxUSD !== null) out.awardMaxUSD = f.awardMaxUSD;
    if (f.includeUnstatedAward) out.includeUnstatedAward = true;
    if (f.awardUnstatedOnly) out.awardUnstatedOnly = true;
    if (f.includeClosed) out.includeClosed = true;
    if (f.fromQuery && f.fromQuery.length > 0) out.fromQuery = f.fromQuery;
    if (f.recommendedOnly) out.recommendedOnly = true;
    return out;
}

/**
 * `ResetFilters` is generic over `Record<string, unknown>` — `ResearchGrantFilterInput`
 * is structurally compatible (every field is an array, boolean, or optional
 * scalar) but, as an `interface` with no index signature, isn't assignable to
 * that constraint without a cast. The cast is safe because `ResetFilters` only
 * reads `Object.keys`/`typeof` per value, never anything index-signature-shaped.
 */
function ResetGrantFilters(filters: ResearchGrantFilterInput): ResearchGrantFilterInput {
    return ResetFilters(filters as unknown as Record<string, unknown>) as unknown as ResearchGrantFilterInput;
}

/**
 * Grant Finder — member-facing grant discovery over the AGU grants corpus
 * (`Recommendation.Content` where Source = 'Grants Latest', 6,527 rows on
 * stage). Matching is vector similarity against the member's typed query
 * and/or attached documents, served by `ResearchApiService.SearchGrants`.
 *
 * Root component of the `grants` module — rendered via `ngComponentOutlet` by
 * `RaSurfaceComponent` with no inputs, so every dependency it needs
 * (`ExplorerRaHostAdapter`, `ResearchApiService`, `MemberItemStateService`)
 * comes through constructor injection from the surface's providers.
 *
 * Product states (PD §5): skeletons while a list loads, the error panel in place of a list that failed, and the empty panels from
 * `state-copy.ts`. Truthfulness edits here: the all-empty panel drops "Save as an alert" (P/proto-states.js:97) and the
 * recommended-empty panel drops "and we will email you the moment one matches" (:98) — the hub has no alerts or match emails.
 */
/**
 * The phone quick chips' groups: the first four of this page's rail group labels, in rail order, without the `Show` toggles
 * (`chips()`, P/Member_Hub.html:918-920; labels as grant-filters.component.html writes them). The chips skip everything inside the
 * "More filters" fold (`!g.closest('#moreF')`, :919), and "Career stage" (P/grant-finder.html:1034) is not a filter in this build, so
 * Grants has three, not four.
 */
export const GRANT_PHONE_FILTER_GROUPS: readonly string[] = ['Funder type', 'Funder / agency', 'Closing date'];

/** Which rail group (by its label) each active-filter pill key belongs to, for the phone chips' `.on` state (P/Member_Hub.html:939-945). */
export const GRANT_PILL_GROUP: Readonly<Record<string, string>> = {
    funderType: 'Funder type',
    grantor: 'Funder / agency',
    excludeGrantor: 'Funder / agency',
    postedAfter: 'Date posted',
    closingBefore: 'Closing date',
    closingAfter: 'Closing date',
    rollingOnly: 'Closing date',
    award: 'Award amount',
    eligibility: 'Eligible applicants',
    region: 'State / region',
};

@Component({
    standalone: false,
    selector: 'ra-grant-finder',
    templateUrl: './grant-finder-page.component.html',
    styleUrl: './grant-finder-page.component.scss',
    // data-accent turns this module's colour into the --accent cascade every
    // shared component below reads (the token layer's ra-module-accent block).
    // ra-fullwidth drops the centred 1200px reading column: EVERY mockup screen
    // is full-bleed inside `.main` (`padding:30px 32px 36px`, no max-width
    // anywhere), the sub-views included — `detail.html:29` sets
    // `.shell { max-width:none }` and caps only the inner `.detail.wide` block
    // at 900px, left-aligned. That inner measure lives on the detail view's own
    // :host (modules/shared/_detail-view.scss), so the host class is
    // unconditional here (review I-2).
    host: {
        '[attr.data-accent]': 'Accent',
        class: 'ra-fullwidth',
    },
})
export class GrantFinderPageComponent implements OnInit, OnDestroy {
    /** The results region this page's tab strip drives, unique per instance so two RA tabs never collide on `aria-controls` (review M-9). */
    readonly ResultsPanelId = NextResultsPanelId('grants');

    readonly TryChips = TRY_CHIPS;

    /** This module's colour, read off its own module definition (never restated here). */
    get Accent(): AGUAppModuleAccent {
        return this.composition.AccentOf(RA_MODULE_KEYS.Grants);
    }

    readonly SortOptions = SORT_OPTIONS;
    /** The sorts for the phone chips' select (P/Member_Hub.html:926-932). */
    readonly PhoneSortOptions: readonly AGUSortOption[] = ToSortOptions(SORT_OPTIONS);
    /** The rail groups the phone chips open the sheet at. */
    readonly FilterGroups = GRANT_PHONE_FILTER_GROUPS;
    /** Phone layout and the filter sheet's open state (P/Member_Hub.html:603-620, 915-951). Assigned in the constructor body. */
    readonly Phone: PhoneFinderChrome;
    /** Bumped when a result set lands or the tab switches; cards rise on it (P/Member_Hub.html:1807-1845). */
    RiseStamp = 0;
    /** Bumped on a tab switch only; the results section slides by RevealDirectionValue (P/grant-finder.html:1219-1232). */
    RevealStamp = 0;
    RevealDirectionValue = 0;

    state: FinderPageState<ResearchGrantFilterInput> = InitialFinderState(EMPTY_GRANT_FILTERS, 'best', 12);
    view: GrantFinderView = 'search';
    recordID: string | null = null;

    /** Constraints the similarity engine inferred from the last understood query but did not apply as a hard filter — rendered as "not yet applied" pills (spec D9), never sent back to the server. */
    judged: JudgedConstraint[] = [];

    items: ResearchGrantMatch[] = [];
    total = 0;
    /** `ResearchGrantSearchResult.queryMode` from the last All-tab search — 'unrecognized' drives `IsUnrecognized` (the "We couldn't understand that search." empty state). */
    queryMode = 'none';

    recommendedItems: ResearchGrantMatch[] = [];
    /** `ResearchRecommendations.mode` — 'none' means the member has no profile vector and typed no query, so recommendations legitimately can't run yet. */
    recommendedMode = 'none';
    private recommendedLoaded = false;
    /** Where each dismissed card sat, so Undo restores it in place (J-3). */
    private readonly dismissedSlots = new DismissedSlots<ResearchGrantMatch>((i) => i.contentID);
    /** Every grant the member has pinned (`researchPinnedGrants`), loaded with the recommendations. */
    private pinnedGrants: ResearchGrantMatch[] = [];
    /** In flight: SetTab's URL sync comes back through OnLocationChanged -> SetTab before the first load lands (one click sent ResearchRecommendations twice). */
    private recommendedLoading = false;

    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();

    /** The product-state copy for this finder (P/proto-states.js:97-98, 115, 121). */
    readonly AllEmptyCopy: StatePanelCopy = FinderAllEmptyCopy(STATE_NOUN.Grants);
    readonly RecommendedEmptyCopy: StatePanelCopy = FinderRecommendedEmptyCopy(STATE_NOUN.Grants);
    readonly ErrorCopy: StatePanelCopy = ListErrorCopy(STATE_NOUN.Grants);
    readonly GoneCopy: StatePanelCopy = LISTING_GONE_COPY;
    readonly BrowseAll = BrowseAllLabel(STATE_NOUN.Grants);

    facets: ResearchGrantFacets | null = null;

    attachment: AGUSearchAttachment | null = null;
    private documentID: string | null = null;

    showNewOnly = false;
    showSavedOnly = false;

    private locationSub: Subscription | null = null;
    private stateSub: Subscription | null = null;
    /**
     * Whether a search has actually run on the All tab. False on a freshly
     * opened module, which is what `IsPreSearch` renders — the page used to fire
     * an EMPTY-query search on open and render "No grants match your filters."
     * over the result, which reads as a verdict on a search the member never
     * made (live finding #9). Nothing is requested until they ask for something.
     */
    private searchRan = false;
    /** Per-tab scroll offsets (CSV 2026-10-24, Grant Finder row 1). Assigned in the constructor body: a field initializer would read `this.host` before TS assigns constructor-parameter properties (see `LastDismissed$`). */
    private readonly tabScroll: FinderTabScrollMemory;

    constructor(
        private readonly api: ResearchApiService,
        private readonly itemState: MemberItemStateService,
        private readonly host: ExplorerRaHostAdapter,
        private readonly composition: RaCompositionState,
        private readonly render: AGURenderService,
        @Optional() @Inject(AGUViewportService) viewport: AGUViewportService | null = null,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The Featured placements' session state, surface-provided (PD §3); absent outside a surface. */
        @Optional() @Inject(AGUAdSessionService) private readonly ads: AGUAdSessionService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        this.Phone = new PhoneFinderChrome(viewport, () => this.render.Notify());
        this.tabScroll = new FinderTabScrollMemory(host);
    }

    ngOnInit(): void {
        this.Phone.Start();
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
        this.stateSub = this.itemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        void this.LoadFacets();
        void this.failures.ReadSupport(this.portalConfig);
        // The Recommended pill carries its count from first paint, as in the mockup (grants.html:446,
        // P-G4/P-C3/P-J3): load in the background; the tab shows progress/errors only while it is open.
        if (!this.recommendedLoaded && !this.recommendedLoading) void this.LoadRecommended();
    }

    ngOnDestroy(): void {
        this.locationSub?.unsubscribe();
        this.stateSub?.unsubscribe();
        this.Phone.Stop();
    }

    /**
     * A getter, not a field initializer: a field initializer reading
     * `this.itemState` runs before TypeScript assigns constructor parameter
     * properties (`this.itemState = itemState` is emitted inside the
     * constructor body, after field initializers that precede the
     * constructor in source order), so it would read `undefined`. The
     * underlying `MemberItemStateService.LastDismissed$` is itself a stable
     * field (not a getter), so re-evaluating this each change-detection pass
     * still returns the same Observable instance — the async pipe below
     * never resubscribes needlessly.
     */
    get LastDismissed$() {
        return this.itemState.LastDismissed$;
    }

    // ── Location / navigation ───────────────────────────────────────────

    private OnLocationChanged(loc: AGUAppLocation): void {
        if (loc.ModuleKey !== RA_MODULE_KEYS.Grants) return;

        if (loc.View === 'detail') {
            this.view = 'detail';
            this.recordID = loc.RecordID ?? null;
            return;
        }

        this.view = 'search';
        this.recordID = null;

        if (loc.RecordID === 'recommended') {
            this.SetTab('recommended');
        }
    }

    /**
     * Leaves the grant view for where it was opened from (`From`), else this finder's search view — the crumb's first ancestor
     * (PD §6). The "no grant specified" state's way out; the loaded view leaves through its own crumb. Never `host.Back()`: the
     * crumb names a place, and the browser's Back still walks history through the URL-synced adapter.
     */
    LeaveDetail(): void {
        this.host.NavigateTo(OriginOf(this.host.CurrentLocation, 'search'));
    }

    /** The gone-listing panel's "See similar" (P/proto-states.js:121): this finder's All tab. */
    OnSeeSimilar(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'search' });
        this.SetTab('all');
    }

    /** The gone-listing panel's "Back to Home" (P/proto-states.js:121). */
    OnBackToHome(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Dashboard, View: 'home' });
    }

    /** "Widen research areas" (P/proto-states.js:97-98): the member's profile, where the research areas show. */
    OnWidenResearchAreas(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    /**
     * The detail view's "Recommended" chip (QA 2026-10-05): only for a grant opened from the
     * Recommended tab that is among this member's recommendations — the same rule the cards
     * follow (`[Recommended]="state.tab === 'recommended'"`). Pinning or "new" never sets it.
     */
    get DetailIsRecommended(): boolean {
        return this.state.tab === 'recommended' && this.recommendedItems.some((i) => i.contentID === this.recordID);
    }

    OnOpen(item: ResearchGrantMatch): void {
        // The finder place on screen (its tab) is the crumb's origin; the module stays mounted, so its query and filters survive.
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'detail', RecordID: item.contentID, From: this.host.CurrentLocation });
    }

    // ── Tabs ─────────────────────────────────────────────────────────────

    get Tabs(): AGUTabStripTab[] {
        return [
            { Key: 'all', Label: 'All grants', Count: this.total },
            // Finding #13 (Task 15 round 1): `recommendedItems` is empty until
            // `LoadRecommended()` runs on the first switch TO this tab, so binding the
            // badge straight to its length made a member with recommendations waiting
            // read `Recommended 0` — while Home, in the same session, said
            // `See 12 recommended` for the same items. `null` renders NO badge
            // (`AGUTabStripTab.Count`: "Absent or null renders no badge — 0 is a real,
            // renderable count"), which is the honest state before the load; after it,
            // a genuine 0 still shows as 0. Home's own `{N}` is the requested page size
            // (`research.recommend.count`), not a count of this member's rows, so
            // seeding the badge from it would render a fabricated number.
            { Key: 'recommended', Label: 'Recommended', Count: this.recommendedLoaded ? this.recommendedItems.length : null },
        ];
    }

    OnTabChange(key: string): void {
        if (key === 'all' || key === 'recommended') this.SetTab(key);
    }

    SetTab(tab: FinderTab): void {
        if (this.state.tab !== tab) {
            this.tabScroll.Switch(this.state.tab, tab);
            // P/grant-finder.html:1219-1232: the incoming section slides in from the side its tab sits on.
            this.RevealDirectionValue = RevealDirection(TAB_ORDER.indexOf(this.state.tab), TAB_ORDER.indexOf(tab));
            this.RevealStamp++; // the cards are re-created on the switch and rise on init through the reveal's first-8 pass
            this.state = { ...this.state, tab };
        }
        if (tab === 'recommended' && !this.recommendedLoaded) {
            if (this.recommendedLoading) this.loading = true;
            else void this.LoadRecommended();
        }
        this.SyncTabToLocation();
    }

    /**
     * Keeps the address bar's `id=recommended` in step with the tab on screen (D-20/G-15).
     * A tab switch and a fresh query (`WithQuery` lands on All) used to change state only, so
     * the URL kept a stale `id=recommended` and browser Back re-applied the Recommended tab
     * the member had already left. `ResetTo` replaces the entry rather than pushing one: a
     * tab is a view of the same place, not somewhere Back should walk through. Its own
     * `Location$` emission comes back through `OnLocationChanged` with the URL already
     * agreeing with the tab, so the loop ends there.
     */
    private SyncTabToLocation(): void {
        const loc = this.host.CurrentLocation;
        if (!loc || loc.ModuleKey !== RA_MODULE_KEYS.Grants || loc.View !== 'search') return;
        const onRecommended = this.state.tab === 'recommended';
        if ((loc.RecordID === 'recommended') === onRecommended) return;
        this.host.ResetTo({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'search', RecordID: onRecommended ? 'recommended' : undefined });
    }

    // ── Search / recommendations ────────────────────────────────────────

    /**
     * The topic text the LAST understood search actually embedded, or `null`
     * when no understood search is standing. Design D10: a follow-up request
     * (page, sort, pill removal, rail edit) re-searches WITHOUT re-extracting —
     * which must mean "reuse what was already extracted", not "re-embed the raw
     * sentence". Before this, page 2 of `hydrology grants, not NIH` embedded the
     * whole sentence while page 1 had embedded `hydrology grants`, so the two
     * pages came from different vectors and `total` drifted 117 → 116
     * (Task 15 round 1, D-6).
     */
    private understoodText: string | null = null;

    /** Alternates the last understood search embedded — re-sent on follow-ups so every page reads one cached ranking. */
    private understoodAlternates: string[] = [];

    /** The grant name the last understood search recognised (QA 2026-10-05) — re-sent on follow-ups so its grants stay pinned first on every page. */
    private understoodNamedGrant: string | null = null;

    /** The raw typed text on a fresh submit; the standing understood topic otherwise. */
    private QueryTextForServer(understand: boolean): string {
        return understand || this.understoodText === null ? this.state.query : this.understoodText;
    }

    /**
     * What to search WITH. Typed search never mixes in the member's profile vector (spec G1) —
     * a constraint-only query ("NOAA opportunities closing this fall") lists every grant
     * matching the constraints instead (spec G3), so `useProfile` is never sent for grants.
     *
     * `alternates` re-sends the topical alternate phrasings the last understood search
     * embedded (`understoodAlternates`) on every follow-up, so paging/sorting/pill-removal all
     * read the one ranking that fresh submit's `alternates` cached — a fresh submit
     * (`understand: true`) sends none, since the server re-extracts and returns its own.
     */
    private QuerySourceForServer(understand: boolean): ResearchQueryInput {
        const text = this.QueryTextForServer(understand);
        return { text, documentIDs: this.state.documentIDs, alternates: understand ? null : this.understoodAlternates };
    }

    /**
     * Whether a follow-up has anything left to ask the server: a topic, a document, or a
     * standing filter. With none the server answers `queryMode 'none'` (grantSearch.ts), which
     * used to render a "0 grants" verdict on a search the member no longer has (G-3 class).
     */
    private HasSomethingToSearch(): boolean {
        const source = this.QuerySourceForServer(false);
        return (source.text ?? '').trim().length > 0 || (source.documentIDs?.length ?? 0) > 0 || this.ActiveFilterCount > 0;
    }

    /** Back to the module as it opened (`IsPreSearch`) — no request, no result verdict, an empty search box. */
    private ReturnToPreSearch(): void {
        this.searchRan = false;
        this.items = [];
        this.total = 0;
        this.queryMode = 'none';
        this.error = null;
        this.judged = [];
        this.understoodText = null;
        this.understoodAlternates = [];
        this.understoodNamedGrant = null;
        this.state = { ...this.state, query: '', page: 1 };
    }

    async Search(understand = false): Promise<void> {
        if (!understand && !this.HasSomethingToSearch()) {
            this.ReturnToPreSearch();
            return;
        }
        this.searchRan = true;
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchGrants({
                query: this.QuerySourceForServer(understand),
                count: this.state.pageSize,
                page: this.state.page,
                filters: this.ActiveFiltersForServer(),
                sort: this.state.sort as GrantSort,
                understand,
                namedGrant: understand ? null : this.understoodNamedGrant,
            });
            if (result.understood) {
                // Spec D9: the server's merged echo IS the rail state now (query wins its group); judged constraints render as "not yet applied" pills.
                this.state = { ...this.state, filters: FiltersFromEcho(result.understood.filters, EMPTY_GRANT_FILTERS) };
                this.judged = result.understood.judged;
                this.understoodText = result.understood.searchText;
                this.understoodAlternates = result.understood.alternates;
                this.understoodNamedGrant = result.understood.namedGrant;
            }
            this.items = result.results;
            this.dismissedSlots.Clear();
            this.total = result.total ?? result.results.length;
            this.queryMode = result.queryMode;
            this.itemState.Prime(
                GRANT_KIND,
                this.items.map((i) => ({ key: i.contentID, itemState: i.itemState }))
            );
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
            this.RiseStamp++; // a fresh result set rises (P/Member_Hub.html:1830)
        }
    }

    async LoadRecommended(): Promise<void> {
        this.recommendedLoading = true;
        const shown = this.state.tab === 'recommended';
        if (shown) {
            this.loading = true;
            this.error = null;
        }
        try {
            const [result, pinned] = await Promise.all([this.api.Recommendations([GRANT_KIND], RECOMMENDED_COUNT), this.api.PinnedGrants()]);
            AssertRecommendationsRefreshed(result, GRANT_KIND); // a kind the server could not refresh is a failed load, not an empty list
            this.recommendedItems = result.grants;
            this.pinnedGrants = pinned;
            this.itemState.Prime(GRANT_KIND, pinned.map((i) => ({ key: i.contentID, itemState: i.itemState })));
            this.recommendedMode = result.mode;
            this.recommendedLoaded = true;
            this.itemState.Prime(
                GRANT_KIND,
                this.recommendedItems.map((i) => ({ key: i.contentID, itemState: i.itemState }))
            );
            if (this.state.tab === 'recommended') this.failures.Reset();
        } catch (e) {
            // A background load that fails stays unloaded, so opening the tab retries it.
            if (this.state.tab === 'recommended') {
                this.error = DescribeError(e);
                this.failures.Record(e);
            }
        } finally {
            this.recommendedLoading = false;
            if (shown || this.state.tab === 'recommended') {
                this.loading = false;
                this.RiseStamp++; // only when the list on screen is the one that landed; a background prefetch must not replay the All tab's rise
            }
        }
    }

    Retry(): void {
        if (this.state.tab === 'recommended') void this.LoadRecommended();
        else void this.Search();
    }

    private async LoadFacets(): Promise<void> {
        try {
            this.facets = await this.api.GrantFacets();
        } catch {
            // Facets are supplementary (checkbox/dropdown option lists) — a
            // failure degrades the filter panel to empty option lists rather
            // than blocking the page.
        }
    }

    /** Delegates to the exported `ActiveGrantFiltersForServer` (spec coverage lives there). */
    private ActiveFiltersForServer(): ResearchGrantFilterInput {
        return ActiveGrantFiltersForServer(this.state.filters);
    }

    // ── Search hero ──────────────────────────────────────────────────────

    OnQueryChange(query: string): void {
        // The standing extraction belongs to the text that produced it; once the
        // member edits the box it is stale, so follow-up searches go back to the
        // raw text until the next fresh submit re-extracts.
        this.understoodText = null;
        this.understoodAlternates = [];
        this.understoodNamedGrant = null;
        this.state = { ...this.state, query };
    }

    /** Spec D10: a fresh submit re-extracts; the PREVIOUS query's groups belong to the previous text, so drop them first (manual groups survive). */
    private SubmitFresh(query: string): void {
        const stripped = StripQueryGroups(this.state.filters, GRANT_QUERY_GROUP_FIELDS, this.state.filters.fromQuery ?? [], EMPTY_GRANT_FILTERS);
        this.state = { ...WithQuery(this.state, query), filters: stripped };
        this.tabScroll.Clear(); // a fresh result set has no "place" to return to
        this.SyncTabToLocation();
        this.judged = [];
        this.understoodText = null;
        this.understoodAlternates = [];
        this.understoodNamedGrant = null;
        void this.Search(true);
    }

    OnSubmitted(query: string): void {
        this.SubmitFresh(query);
    }

    OnSuggestionSelected(suggestion: string): void {
        this.SubmitFresh(suggestion);
    }

    async OnFileSelected(file: File): Promise<void> {
        if (file.size > MAX_ATTACHMENT_BYTES) {
            this.attachment = { Name: file.name, Status: 'failed', Error: 'Files must be under 10 MB' };
            return;
        }
        this.attachment = { Name: file.name, Status: 'uploading' };
        try {
            const doc = await this.api.UploadDocument(file, 'search', false);
            this.documentID = doc.documentID;
            this.attachment = { Name: doc.fileName, Status: 'ready' };
            this.state = { ...this.state, documentIDs: [doc.documentID] };
        } catch (e) {
            this.attachment = { Name: file.name, Status: 'failed', Error: e instanceof Error ? e.message : 'Upload failed' };
        }
    }

    async OnAttachmentRemoved(): Promise<void> {
        const id = this.documentID;
        this.attachment = null;
        this.documentID = null;
        this.state = { ...this.state, documentIDs: [] };
        if (id) {
            try {
                await this.api.DocumentDelete(id);
            } catch {
                // Best-effort cleanup — the UI has already dropped the attachment
                // either way, so a failed server-side delete isn't worth surfacing.
            }
        }
    }

    // ── Filters / sort / paging ──────────────────────────────────────────

    get ActiveFilterCount(): number {
        return CountActiveGrantFilterGroups(this.state.filters);
    }

    OnFiltersChange(filters: ResearchGrantFilterInput): void {
        this.state = { ...this.state, filters, page: 1 };
        void this.Search();
    }

    /** The rail's "Recommended only" toggle mirrors `filters.recommendedOnly` — a server-side filter on the All grants results (spec G2), not a tab switch. */
    OnRecommendedOnlyChange(on: boolean): void {
        this.state = { ...this.state, filters: { ...this.state.filters, recommendedOnly: on }, page: 1 };
        void this.Search();
    }

    /**
     * Filter pills, then judged pills — and the judged pills on the `All grants` tab ONLY
     * (round 1, finding #8). A judged pill says something about the result set in front of
     * the member ("Career stage: early career · not yet applied"); the `Recommended` tab runs
     * `ResearchRecommendations` and no search at all, so the same pill there is a claim about
     * a set the constraint never touched. The judged list itself is untouched by the tab
     * switch — switching back to `All grants` shows it again, because the search it belongs
     * to is still the standing one.
     */
    /** The rail groups with at least one active filter — the phone chips render those `.on` (P/Member_Hub.html:653, 939-945). */
    get ActiveFilterGroups(): readonly string[] {
        return ActiveGroupsFrom(this.FilterPills, GRANT_PILL_GROUP);
    }

    get FilterPills(): ActiveFilterPill[] {
        const pills = BuildGrantFilterPills(this.state.filters);
        return this.state.tab === 'all' ? [...pills, ...JudgedPills(this.judged)] : pills;
    }

    OnRemoveFilterGroup(key: string): void {
        if (IsJudgedKey(key)) {
            this.judged = RemoveJudged(this.judged, key);
            return;
        }
        this.state = { ...this.state, filters: RemoveGrantFilterGroup(this.state.filters, key), page: 1 };
        void this.Search();
    }

    /**
     * `Clear all` drops every constraint, the query-derived ones included (spec D9) — so the
     * standing extraction (and its alternates) goes with them, exactly as a fresh submit drops
     * it. The raw text is still in the hero, so that is what gets searched.
     */
    /**
     * Mockup `resetAll` (grants.html:980-988): clears the filters AND the query and its box.
     * Keeping the query with its understood topic nulled re-sent the raw sentence, so the
     * constraint words were embedded as the topic. An attached document still searches;
     * otherwise the page returns to its opening state with no request (`Search`).
     */
    OnClearFilters(): void {
        this.state = { ...this.state, query: '', filters: ResetGrantFilters(this.state.filters), page: 1 };
        this.judged = [];
        this.understoodText = null;
        this.understoodAlternates = [];
        this.understoodNamedGrant = null;
        this.showNewOnly = false;
        this.showSavedOnly = false;
        void this.Search();
    }

    OnSortChange(sort: string): void {
        this.state = { ...this.state, sort, page: 1 };
        void this.Search();
    }

    OnPageChange(page: number): void {
        this.state = WithPage(this.state, page);
        void this.Search();
    }

    // ── Display lists / empty states ────────────────────────────────────

    /**
     * Scoped to the Recommended tab only — on the All tab this must be empty
     * even if a Recommended item happens to be pinned, otherwise `IsEmpty`
     * below stays false when the All tab's own filtered results are empty
     * (empty grid + pagination showing Total 0 instead of the "No grants
     * match your filters" state — review finding, 2026-09-03).
     */
    get PinnedItems(): ResearchGrantMatch[] {
        if (this.state.tab !== 'recommended') return [];
        // Every pinned grant, not only pinned recommendations: the mockup's renderRec lists
        // `G.filter(g => g.pinned && !g.dismissed)` (grants.html:800-804, P-G9 2026-10-08).
        const byId = new Map<string, ResearchGrantMatch>();
        for (const i of [...this.pinnedGrants, ...this.recommendedItems]) {
            if (i.itemState.pinned && !i.itemState.dismissed && !byId.has(i.contentID)) byId.set(i.contentID, i);
        }
        return [...byId.values()];
    }

    /** Whether a card is one of the member's recommendations: a pinned grant that is not keeps no Recommended chip (C-16). */
    IsRecommendation(item: ResearchGrantMatch): boolean {
        return this.recommendedItems.some((i) => i.contentID === item.contentID);
    }

    private get UnpinnedRecommended(): ResearchGrantMatch[] {
        return this.recommendedItems.filter((i) => !i.itemState.pinned);
    }

    /**
     * The All tab hoists pinned results to the front of the page it is showing
     * (Ruling R-F12, `PinnedFirst`) — the Recommended tab lifts them into its
     * own "Pinned by you" section instead, so it must not reorder as well.
     */
    get DisplayItems(): ResearchGrantMatch[] {
        const source = this.state.tab === 'recommended' ? this.UnpinnedRecommended : PinnedFirst(this.items);
        return source.filter((i) => (!this.showNewOnly || i.isNew) && (!this.showSavedOnly || i.itemState.saved));
    }

    /**
     * The All tab before the member has asked for anything. Distinct from
     * "no results": nothing has been searched, so there is nothing to report an
     * absence of, and offering "Clear all filters" for filters they never set
     * only confuses (live finding #9).
     */
    /**
     * The landing strip under the search hero (P/Member_Hub.html:1383-1401): shown while the finder is in its landing state —
     * Recommended, or All before any search — and gone once results show, as the prototype hides it while `#searchSec` is visible.
     */
    get HeroStrip(): AGUAdPlacement | null {
        const landing = this.state.tab === 'recommended' || this.IsPreSearch;
        return landing ? (this.ads?.First('grants', 'strip') ?? null) : null;
    }

    get IsPreSearch(): boolean {
        return this.state.tab === 'all' && !this.searchRan;
    }

    /**
     * The toolbar's count line (grants.html:960) — All tab only, and only once a
     * search has actually run.
     *
     * The on-page count is `DisplayItems.length`, the count of what the column
     * actually renders, NOT `items.length`, the unfiltered page. The Show
     * switches (`showNewOnly`/`showSavedOnly`) filter the page client-side, so
     * reading the raw page made the line contradict the list in front of the
     * member — "Saved only" with nothing saved printed `117 grants · showing 1
     * to 9` over an empty column (final review, finding #1). `Total` stays the
     * corpus total: that is what the engine counted, and it is not a claim
     * about this page.
     */
    get ResultCount(): AGUResultCount | null {
        // A failed load shows no count (P/proto-states.js:117, `#count` '').
        if (this.state.tab !== 'all' || this.IsPreSearch || this.error) return null;
        return PageResultCount(this.total, this.state.page, this.state.pageSize, this.DisplayItems.length, 'grant');
    }

    get ShowNothingToMatchYet(): boolean {
        return this.state.tab === 'recommended' && this.recommendedMode === 'none' && this.recommendedItems.length === 0;
    }

    /** Spec 2026-09-30 F1: the intake judged the typed text unsearchable. */
    get IsUnrecognized(): boolean {
        return this.state.tab === 'all' && this.queryMode === 'unrecognized';
    }

    get IsEmpty(): boolean {
        return this.DisplayItems.length === 0 && this.PinnedItems.length === 0;
    }

    // ── Card actions ─────────────────────────────────────────────────────

    async OnSave(item: ResearchGrantMatch, saved: boolean): Promise<void> {
        await this.itemState.Toggle(GRANT_KIND, item.contentID, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966), once the save has stuck: a failed Toggle throws.
    }

    async OnPin(item: ResearchGrantMatch, pinned: boolean): Promise<void> {
        await this.itemState.Toggle(GRANT_KIND, item.contentID, { pinned });
    }

    async OnDismiss(item: ResearchGrantMatch): Promise<void> {
        await this.itemState.Dismiss(GRANT_KIND, item.contentID, item.title);
        const left = this.dismissedSlots.Remove(item, this.items, this.recommendedItems);
        this.items = left.Items;
        this.recommendedItems = left.Recommended;
    }

    async Undo(): Promise<void> {
        await this.itemState.Undo();
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        if (change.kind !== GRANT_KIND) return;
        const patch = (list: ResearchGrantMatch[]): void => {
            const item = list.find((i) => i.contentID === change.key);
            if (item) item.itemState = change.itemState;
        };
        patch(this.items);
        patch(this.recommendedItems);
        patch(this.pinnedGrants);
        // Pinned from the All tab: carry it into the Recommended tab's `Pinned by you` section.
        if (change.itemState.pinned && !this.pinnedGrants.some((i) => i.contentID === change.key)) {
            const source = this.items.find((i) => i.contentID === change.key) ?? this.recommendedItems.find((i) => i.contentID === change.key);
            if (source) this.pinnedGrants = [{ ...source, itemState: change.itemState }, ...this.pinnedGrants];
        }
        // Pinning repartitions the All tab (`DisplayItems` → `PinnedFirst`), and
        // the server-truth emission arrives after an await, outside the click
        // that started it — in a zoneless host nothing else would repaint it.
        this.render.Notify();

        // `confirmed` only, deliberately: every toggle emits twice (optimistic,
        // then server truth), and reloading on both fired two identical searches
        // for one Undo (live finding #18) — the first of which raced the write it
        // was reacting to.
        if (change.confirmed && !change.itemState.dismissed) {
            // A dismissed card is spliced out of `items`/`recommendedItems`, so Undo has nothing left to
            // patch above. Put it back in the slot it left (J-3: a reload re-ranks, and the restored item
            // can land off the page); reload only for an item this page never removed itself.
            const restored = this.dismissedSlots.Restore(change.key, this.items, this.recommendedItems, (i) => ({ ...i, itemState: change.itemState }));
            if (restored) {
                this.items = restored.Items;
                this.recommendedItems = restored.Recommended;
                this.render.Notify();
                return;
            }
            const wasTracked = this.items.some((i) => i.contentID === change.key) || this.recommendedItems.some((i) => i.contentID === change.key);
            // Only an undone dismissal can bring back an item this page does not hold; a save or pin elsewhere (a Saved-board Undo)
            // never re-runs this module's search (F-11).
            if (!wasTracked && change.undismissed && this.view === 'search') {
                if (this.state.tab === 'recommended') void this.LoadRecommended();
                else void this.Search();
            }
        }
    }
}
